import { useState, useEffect } from 'react';
import { Users, Building2, AlertTriangle, ShoppingCart, Layers, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import { getGlobalSettings, toggleCommunitySection, toggleOrganizationsSection, toggleBuySellFormMode, toggleSimplifiedFormEnabled } from 'wasp/client/operations';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';

const CommunitySectionTab = () => {
  const [loading, setLoading] = useState(true);
  const [savingCommunity, setSavingCommunity] = useState(false);
  const [savingOrganizations, setSavingOrganizations] = useState(false);
  const [savingFormMode, setSavingFormMode] = useState(false);
  const [savingSimplified, setSavingSimplified] = useState(false);
  const [communityEnabled, setCommunityEnabled] = useState(true);
  const [organizationsEnabled, setOrganizationsEnabled] = useState(true);
  const [buySellFormMode, setBuySellFormMode] = useState<'single' | 'wizard'>('single');
  const [simplifiedEnabled, setSimplifiedEnabled] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settings = await getGlobalSettings();

      const communitySetting = settings.find((s: any) => s.settingKey === 'community_section_enabled');
      if (communitySetting) {
        setCommunityEnabled((communitySetting.settingValue as { enabled: boolean }).enabled ?? true);
      }

      const orgsSetting = settings.find((s: any) => s.settingKey === 'organizations_section_enabled');
      if (orgsSetting) {
        setOrganizationsEnabled((orgsSetting.settingValue as { enabled: boolean }).enabled ?? true);
      }

      const formModeSetting = settings.find((s: any) => s.settingKey === 'buy_sell_form_mode');
      if (formModeSetting) {
        setBuySellFormMode((formModeSetting.settingValue as { mode: 'single' | 'wizard' }).mode ?? 'single');
      }

      const simplifiedSetting = settings.find((s: any) => s.settingKey === 'simplified_submission_enabled');
      if (simplifiedSetting) {
        setSimplifiedEnabled((simplifiedSetting.settingValue as { enabled: boolean }).enabled ?? false);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load section settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCommunityToggle = async () => {
    const newEnabled = !communityEnabled;
    setCommunityEnabled(newEnabled);
    try {
      setSavingCommunity(true);
      await toggleCommunitySection({ enabled: newEnabled });
      toast.success(newEnabled ? 'Community section enabled' : 'Community section hidden');
    } catch (error: any) {
      toast.error(error.message || 'Failed to toggle community section');
      setCommunityEnabled(!newEnabled);
    } finally {
      setSavingCommunity(false);
    }
  };

  const handleOrganizationsToggle = async () => {
    const newEnabled = !organizationsEnabled;
    setOrganizationsEnabled(newEnabled);
    try {
      setSavingOrganizations(true);
      await toggleOrganizationsSection({ enabled: newEnabled });
      toast.success(newEnabled ? 'Organizations section enabled' : 'Organizations section hidden');
    } catch (error: any) {
      toast.error(error.message || 'Failed to toggle organizations section');
      setOrganizationsEnabled(!newEnabled);
    } finally {
      setSavingOrganizations(false);
    }
  };

  const handleFormModeToggle = async () => {
    const newMode: 'single' | 'wizard' = buySellFormMode === 'single' ? 'wizard' : 'single';
    setBuySellFormMode(newMode);
    try {
      setSavingFormMode(true);
      await toggleBuySellFormMode({ mode: newMode });
      toast.success(
        newMode === 'wizard'
          ? 'Buy & Sell now uses the multi-step wizard form'
          : 'Buy & Sell reverted to single-page form'
      );
    } catch (error: any) {
      toast.error(error.message || 'Failed to update form mode');
      setBuySellFormMode(buySellFormMode === 'single' ? 'single' : 'wizard'); // revert
    } finally {
      setSavingFormMode(false);
    }
  };

  const handleSimplifiedToggle = async () => {
    const newEnabled = !simplifiedEnabled;
    setSimplifiedEnabled(newEnabled);
    try {
      setSavingSimplified(true);
      await toggleSimplifiedFormEnabled({ enabled: newEnabled });
      toast.success(
        newEnabled
          ? 'Quick Submit option enabled for users'
          : 'Quick Submit option disabled'
      );
    } catch (error: any) {
      toast.error(error.message || 'Failed to update simplified submission setting');
      setSimplifiedEnabled(!newEnabled);
    } finally {
      setSavingSimplified(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">Loading section settings...</p>
      </div>
    );
  }

  const bothDisabled = !communityEnabled && !organizationsEnabled;

  return (
    <div className="px-6 py-6 space-y-6 w-full">
      {bothDisabled && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Both sections are hidden</AlertTitle>
          <AlertDescription>
            Regular users will only see the Information and Directory sections. Enable at least one section to restore the nav toggle.
          </AlertDescription>
        </Alert>
      )}

      {/* Community Toggle */}
      <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            <h3 className="font-semibold leading-none tracking-tight">Community Section</h3>
          </div>
          <Switch
            checked={communityEnabled}
            onCheckedChange={handleCommunityToggle}
            disabled={savingCommunity}
            className="ml-auto"
          />
        </div>
        <p className="text-sm text-muted-foreground mt-1.5">
          Show or hide the Community tab — Buy &amp; Sell, Request &amp; Offer, Ask for Help, Lost &amp; Found, Tools Sharing, Transportation, Housing, Professionals.
        </p>
        {!communityEnabled && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            Hidden for regular users. Admins can still see it.
          </p>
        )}
      </div>

      {/* Organizations Toggle */}
      <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            <h3 className="font-semibold leading-none tracking-tight">Organizations Section</h3>
          </div>
          <Switch
            checked={organizationsEnabled}
            onCheckedChange={handleOrganizationsToggle}
            disabled={savingOrganizations}
            className="ml-auto"
          />
        </div>
        <p className="text-sm text-muted-foreground mt-1.5">
          Show or hide the Organizations tab — Community Services directory, org listings, and organization profiles.
        </p>
        {!organizationsEnabled && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            Hidden for regular users. Admins can still see it.
          </p>
        )}
      </div>

      {/* Buy & Sell Form Mode Toggle */}
      <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            <h3 className="font-semibold leading-none tracking-tight">Community Forms — Form Style</h3>
          </div>
          <Switch
            checked={buySellFormMode === 'wizard'}
            onCheckedChange={handleFormModeToggle}
            disabled={savingFormMode}
            className="ml-auto"
          />
        </div>
        <p className="text-sm text-muted-foreground mt-1.5">
          Toggle the posting form between <strong>Single-Page</strong> (classic, all fields visible at once) and{' '}
          <strong>Multi-Step Wizard</strong> (guided, step-by-step — like filling in an application).
        </p>
        <div className="mt-2 flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
              buySellFormMode === 'wizard'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
            }`}
          >
            <Layers className="h-3 w-3" />
            {buySellFormMode === 'wizard' ? 'Multi-Step Wizard active' : 'Single-Page Form active'}
          </span>
          {savingFormMode && (
            <span className="text-xs text-muted-foreground animate-pulse">Saving…</span>
          )}
        </div>
      </div>

      {/* Simplified Submission Toggle */}
      <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            <h3 className="font-semibold leading-none tracking-tight">Quick Submit (Simplified Form)</h3>
          </div>
          <Switch
            checked={simplifiedEnabled}
            onCheckedChange={handleSimplifiedToggle}
            disabled={savingSimplified}
            className="ml-auto"
          />
        </div>
        <p className="text-sm text-muted-foreground mt-1.5">
          When enabled, users see a <strong>Quick Submit</strong> option on posting forms — just a description field
          and a submit button. A background process will categorise and fill in the details. Postings submitted
          this way take longer to be published.
        </p>
        <div className="mt-2 flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
              simplifiedEnabled
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
            }`}
          >
            <Zap className="h-3 w-3" />
            {simplifiedEnabled ? 'Quick Submit available to users' : 'Quick Submit hidden'}
          </span>
          {savingSimplified && (
            <span className="text-xs text-muted-foreground animate-pulse">Saving…</span>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="p-4 bg-muted rounded-lg">
        <h4 className="font-medium mb-2">How it works:</h4>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li className="flex gap-2">
            <span className="text-primary">•</span>
            <span><strong>Nav toggle:</strong> The Community / Organizations switcher in the top bar only shows enabled sections. If only one is enabled, the toggle is hidden and that section is shown directly.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-primary">•</span>
            <span><strong>Admins:</strong> Always see both sections regardless of these settings.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-primary">•</span>
            <span><strong>Individual sections:</strong> Use the Section Visibility tab to control individual items within each section.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-primary">•</span>
            <span><strong>Form style:</strong> When the wizard is on, new Buy &amp; Sell postings use a step-by-step guided form. Existing postings are unaffected.</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CommunitySectionTab;



